import { useCallback, useEffect, useRef, useState } from 'react';
import { beatProposes, decisionDetail, irisOutput, nextBeat, scriptTokens, type IrisBeat, type IrisScriptMove, type IrisStep } from '@engineer-os/engine';
import { api } from '../api';
import { track } from '../lib/events';
import type { IrisDecision, IrisEntry } from '../lib/iris';
import type { PublicMission } from '../types';

/**
 * Scripted Iris's loop, for the general levels that carry a script (engine
 * iris-script.ts, docs/iris.md "Scripted levels").
 *
 * The same shape as `useIris` and the same rule: nothing runs except from
 * `approve`, which only the panel's Run it button calls. What differs is where
 * the next step comes from. Here it is the level's `irisScript`, so no request
 * leaves the tab, no account is needed and nothing is spent; the learner
 * answers with the buttons or with one of the replies the author wrote.
 *
 * The conversation belongs to the workspace, for `useIris`'s reason: a level
 * change, a session change or a reset replaces it outright.
 */

export type ScriptedBusy = 'thinking' | 'running' | 'checking' | null;

/**
 * An entry of the log with what the scripted panel draws beside live Iris's:
 * the beat behind a step (its speaker, its `show`, its cost) and the cost of
 * a reply the learner chose. Live Iris's `IrisEntry` keeps its meaning; these
 * fields only ride along, so `Entry` (Iris.tsx) still draws the rest.
 */
export type ScriptedEntry = IrisEntry & { beat?: IrisBeat; tokens?: number };

export interface ScriptedIrisApi {
  log: ScriptedEntry[];
  /** Tokens spent so far: every beat shown and every reply chosen. */
  tokens: number;
  /** The level's `iris_script_budget`, or null. Display only. */
  budget: number | null;
  /** The beat waiting for the learner, or null before the start and after the end. */
  beat: IrisBeat | null;
  started: boolean;
  /** Iris has said everything she had for this level. */
  ended: boolean;
  busy: ScriptedBusy;
  /** The approved command could not be run at all; the beat is still on the table. */
  runFailed: boolean;
  start(): void;
  /** Run it, Check my work, or Go on, whichever the beat offers. */
  approve(reason?: string): void;
  reject(reason?: string): void;
  reply(index: number, reason?: string): void;
  restart(): void;
}

export interface UseScriptedIrisOptions {
  mission: PublicMission;
  sessionId: string | null;
  resetKey: number;
  onRan(): void;
  onCheck(): Promise<string>;
}

/** How long Iris "thinks" before a written line appears. Instant reads as a form, not a colleague. */
const PAUSE_MS = 650;

function asStep(beat: IrisBeat): IrisStep {
  if (beat.run !== undefined) return { say: beat.say, action: { kind: 'run', command: beat.run } };
  if (beat.check) return { say: beat.say, action: { kind: 'check' } };
  return { say: beat.say, action: { kind: 'done' } };
}

function entryFor(beat: IrisBeat): ScriptedEntry {
  return {
    kind: 'step',
    step: asStep(beat),
    decision: beatProposes(beat) ? 'pending' : 'done',
    beat,
    ...(beat.tokens ? { tokens: beat.tokens } : {}),
  };
}

export function useScriptedIris({ mission, sessionId, resetKey, onRan, onCheck }: UseScriptedIrisOptions): ScriptedIrisApi {
  const script = mission.irisScript ?? [];
  const [log, setLog] = useState<ScriptedEntry[]>([]);
  const [beat, setBeat] = useState<IrisBeat | null>(null);
  const [started, setStarted] = useState(false);
  const [busy, setBusyState] = useState<ScriptedBusy>(null);
  const [runFailed, setRunFailed] = useState(false);
  const working = useRef<ScriptedBusy>(null);
  const setBusy = useCallback((next: ScriptedBusy) => {
    working.current = next;
    setBusyState(next);
  }, []);
  // Bumped by every reset, so a pause or a command that finishes after one
  // writes nothing into the conversation that replaced it.
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    generation.current += 1;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setLog([]);
    setBeat(null);
    setStarted(false);
    setBusy(null);
    setRunFailed(false);
  }, [setBusy]);

  useEffect(clear, [mission.id, sessionId, resetKey, clear]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const count = useCallback((detail: string) => {
    track('iris-step', { level: mission.level, track: mission.track, detail });
  }, [mission.level, mission.track]);

  /** Iris says `next` after a pause, or falls silent when it is null. */
  const say = useCallback((next: IrisBeat | null) => {
    const gen = generation.current;
    setBeat(null);
    setBusy('thinking');
    timer.current = setTimeout(() => {
      timer.current = null;
      if (generation.current !== gen) return;
      setBusy(null);
      if (!next) {
        count('done');
        return;
      }
      setLog((l) => [...l, entryFor(next)]);
      setBeat(next);
      if (beatProposes(next)) count('proposed');
    }, PAUSE_MS);
  }, [count, setBusy]);

  /**
   * Settle the newest entry and move on. Every answer passes through here
   * once it has taken effect, so this is where the decision is counted
   * (docs/designed-agents.md, workstream B): a Run it whose command could not
   * be run is no decision yet, and pressing it again must not count twice.
   */
  const move = useCallback((from: IrisBeat, how: IrisScriptMove, decision: Exclude<IrisDecision, 'pending'>, extra: ScriptedEntry[] = []) => {
    const detail = decisionDetail(from, how);
    if (detail) track('agent-decision', { level: mission.level, track: mission.track, detail });
    setLog((l) => [
      ...l.map((e, i) => (i === l.length - 1 && e.kind === 'step' && e.decision === 'pending' ? { ...e, decision } : e)),
      ...extra,
    ]);
    say(nextBeat(script, from, how));
  }, [say, script, mission.level, mission.track]);

  const start = useCallback(() => {
    if (working.current || started || script.length === 0) return;
    setStarted(true);
    say(script[0]!);
  }, [say, script, started]);

  const approve = useCallback(async (reason = '') => {
    const current = beat;
    if (working.current || !current) return;
    const gen = generation.current;
    const note = reason.trim().slice(0, 1000);
    const notes: ScriptedEntry[] = note ? [{ kind: 'said', text: note }] : [];
    setRunFailed(false);
    if (current.run !== undefined) {
      if (!sessionId) return;
      setBusy('running');
      let result: { stdout: string; stderr: string; code: number };
      try {
        result = await api.irisRun(sessionId, current.run);
      } catch {
        if (generation.current === gen) {
          setBusy(null);
          setRunFailed(true);
        }
        return;
      }
      if (generation.current !== gen) return;
      onRan();
      count('ran');
      // The output exactly as live Iris would be shown it, cut where hers is cut.
      const text = irisOutput(current.run, result.code, result.stdout, result.stderr);
      move(current, { kind: 'approve' }, 'ran', [...notes, { kind: 'output', text }]);
      return;
    }
    if (current.check) {
      setBusy('checking');
      let summary: string;
      try {
        summary = await onCheck();
      } catch {
        summary = 'Check my work gave no result.';
      }
      if (generation.current !== gen) return;
      count('check');
      move(current, { kind: 'approve' }, 'checked', [...notes, { kind: 'check', text: summary }]);
      return;
    }
    move(current, { kind: 'approve' }, 'done', notes);
  }, [beat, sessionId, onRan, onCheck, count, move, setBusy]);

  const reject = useCallback((reason = '') => {
    if (working.current || !beat || !beatProposes(beat)) return;
    setRunFailed(false);
    count('rejected');
    const note = reason.trim().slice(0, 1000);
    move(beat, { kind: 'reject' }, 'rejected', note ? [{ kind: 'said', text: note }] : []);
  }, [beat, count, move]);

  const reply = useCallback((index: number, reason = '') => {
    const said = beat?.replies?.[index];
    if (working.current || !beat || !said) return;
    setRunFailed(false);
    count('noted');
    const note = reason.trim().slice(0, 1000);
    move(beat, { kind: 'reply', index }, 'noted', [
      ...(note ? [{ kind: 'said' as const, text: note }] : []),
      { kind: 'said', text: said.say, ...(said.tokens ? { tokens: said.tokens } : {}) },
    ]);
  }, [beat, count, move]);

  return {
    log,
    tokens: scriptTokens(log),
    budget: mission.irisScriptBudget ?? null,
    beat,
    started,
    ended: started && !busy && beat === null,
    busy,
    runFailed,
    start,
    approve: (reason) => void approve(reason),
    reject,
    reply,
    restart: clear,
  };
}
